/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { User } from '../../../models/auth';
import { EncryptionService } from '../../../services/encryption.service';

// Lazy import types only (don’t trigger runtime load during SSR)
import {
  LiveChatWidgetModule,
  type WidgetState,
  type WidgetStateService,
} from '@livechat/widget-angular';
import { I18nService } from '../../../services/i18n.service';

declare global {
  interface Window {
    LC_API?: {
      set_customer_data: (data: any) => void;
      hide_chat_window: () => void;
      destroyChat?: () => void;
      set_custom_variables?: (vars: any[]) => void;
    };
  }
}

@Component({
  selector: 'logibids-live-chat-widget',
  standalone: true,
  imports: [LiveChatWidgetModule],
  templateUrl: './live-chat-widget.component.html',
  styleUrl: './live-chat-widget.component.scss',
})
export class LiveChatWidgetComponent implements OnInit, OnDestroy {
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  profile!: User;
  private stateSubscription?: Subscription;
  chatState: WidgetState | null = null;
  name: string = '';
  email: string = '';
  isBrowser = false;

  private widgetStateService?: WidgetStateService;

  constructor(
    private _authService: AuthService,
    public translate: I18nService,
    private _EncryptionService: EncryptionService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  async ngOnInit(): Promise<void> {
    if (this.isBrowser) {
      // ✅ dynamically import only in browser
      const { WidgetStateService } = await import('@livechat/widget-angular');

      // Fake NgZone shim (since project is zoneless)
      const fakeNgZone = {
        run: (fn: () => any) => fn(),
        runOutsideAngular: (fn: () => any) => fn(),
      } as any;

      this.widgetStateService = new WidgetStateService(fakeNgZone);

      this.stateSubscription = this.widgetStateService.subject.subscribe(
        (state: WidgetState) => {
          this.chatState = state;
        }
      );

      this.profile = this._EncryptionService.decryptLocalStorage('profile');
      if (this.profile) {
        this.email = this.profile.auth_id || '';
        this.name = this.profile.name;
      }
    }
  }

  getCurrentLanguage(): string {
    return this.translate.getCurrentLang();
  }

  clearLiveChatSession(): void {
    if (this.isBrowser) {
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.LC_API) {
          window.LC_API.hide_chat_window?.();

          if (typeof window.LC_API['set_customer_data'] === 'function') {
            window.LC_API['set_customer_data'](null);
          }

          if (typeof window.LC_API?.set_custom_variables === 'function') {
            window.LC_API.set_custom_variables([]);
          } else {
            console.warn('No method available to clear customer data.');
          }
        }
      }, 2000);
    }
  }

  ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe();
    this.clearLiveChatSession();
  }
}
